import { useMemo } from 'react';
import type { EPCR } from '@/types';
import { cn } from '@/lib/cn';
import { Baby, AlertTriangle } from 'lucide-react';
import { RuleTag } from '@/components/RuleTag';

/**
 * PediatricTraumaSubSet — Foundation v1.0 §4.6.
 *
 * Adds pediatric-specific fields on top of the Trauma Set when the
 * patient is under 18. Powers:
 *   - Broselow color lookup (from entered weight in kg)
 *   - Weight-based dosing downstream in the formulary
 *   - Caregiver presence + car-seat appropriateness for registry
 *   - Suspected non-accidental trauma (NAT) + DCYF reporter capture
 *     (RI mandatory reporter law)
 *
 * Activation gating is handled by the conditional cascade engine
 * (trauma_pediatric); this component only renders when the parent
 * TraumaTab decides to mount it.
 */

interface Props {
  epcr: EPCR;
  onUpdate: (patch: Partial<EPCR>) => void;
}

// Broselow color bands per standard weight-based tape (approximate).
// Purely derived — the crew enters weight, we show the color.
const BROSELOW_BANDS: { max: number; color: string; swatch: string }[] = [
  { max: 6.5,   color: 'Grey',   swatch: '#b8bcc4' },
  { max: 8,     color: 'Pink',   swatch: '#f3b5c4' },
  { max: 10,    color: 'Red',    swatch: '#c0392b' },
  { max: 12,    color: 'Purple', swatch: '#8e44ad' },
  { max: 14,    color: 'Yellow', swatch: '#f1c40f' },
  { max: 18,    color: 'White',  swatch: '#ecf0f1' },
  { max: 23,    color: 'Blue',   swatch: '#2980b9' },
  { max: 29,    color: 'Orange', swatch: '#e67e22' },
  { max: 36,    color: 'Green',  swatch: '#27ae60' },
  { max: Infinity, color: 'Adult', swatch: '#7f8c8d' },
];

function broselowFor(weightKg: number | null | undefined): { color: string; swatch: string } | null {
  if (weightKg == null || !Number.isFinite(weightKg) || weightKg <= 0) return null;
  for (const band of BROSELOW_BANDS) {
    if (weightKg <= band.max) return { color: band.color, swatch: band.swatch };
  }
  return null;
}

export function PediatricTraumaSubSet({ epcr, onUpdate }: Props) {
  const locked = epcr.locked;
  const broselow = useMemo(() => broselowFor(epcr.peds_weight_kg), [epcr.peds_weight_kg]);

  // Keep the computed broselow color cached on the record (useful for
  // downstream dose-calc UI). Only write when it actually changes.
  if (broselow && epcr.peds_broselow_color !== broselow.color) {
    // Schedule the write — the parent component will reflect it.
    queueMicrotask(() => onUpdate({ peds_broselow_color: broselow.color }));
  }

  const natFlagged = epcr.peds_suspected_nat === 'yes';

  return (
    <section className="border border-violet-800/60 bg-violet-950/10">
      <header className="flex items-center justify-between border-b border-violet-800/40 bg-violet-950/30 px-3 py-1.5">
        <div className="flex items-center gap-2">
          <Baby size={14} className="text-violet-300" />
          <h3 className="text-[11px] font-bold uppercase tracking-widest text-violet-200">
            Pediatric Trauma Sub-Set
          </h3>
        </div>
        <RuleTag
          sources={[
            { standard: 'ACS-2021', rationale: 'Pediatric-specific triage + destination recommendation' },
            { standard: 'Department', rationale: 'RI DCYF mandatory-reporter law (NAT flag)' },
          ]}
        />
      </header>
      <div className="p-3 space-y-4">
        {/* Weight + Broselow */}
        <div className="grid gap-3 sm:grid-cols-[auto_auto_1fr] items-start">
          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-widest text-slate-500">Weight (kg)</span>
            <input
              type="number"
              inputMode="decimal"
              step="0.1"
              value={epcr.peds_weight_kg ?? ''}
              onChange={(e) => {
                const n = Number(e.target.value);
                onUpdate({ peds_weight_kg: e.target.value === '' ? null : Number.isFinite(n) ? n : null });
              }}
              disabled={locked}
              className="input-field font-mono text-sm w-28"
            />
          </label>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Broselow</div>
            {broselow ? (
              <div className="flex items-center gap-2 mt-1">
                <span
                  className="inline-block h-4 w-4 border border-slate-700"
                  style={{ background: broselow.swatch }}
                  aria-hidden
                />
                <span className="text-slate-100 font-semibold text-sm">{broselow.color}</span>
              </div>
            ) : (
              <div className="text-slate-500 text-xs italic mt-1">enter weight</div>
            )}
          </div>
          <div className="text-[11px] leading-snug text-violet-200/80 bg-violet-950/40 border border-violet-800/40 px-2 py-1.5">
            Weight-based dosing activates for any medication given on this run. The
            formulary surfaces pediatric doses from the protocol engine — no manual
            math required at the bedside.
          </div>
        </div>

        {/* Caregiver + Car Seat */}
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-widest text-slate-500">Caregiver Present</span>
            <select
              value={epcr.peds_caregiver_present ?? ''}
              onChange={(e) => onUpdate({ peds_caregiver_present: e.target.value || null })}
              disabled={locked}
              className="input-field text-sm"
            >
              <option value="">Select…</option>
              <option value="Parent">Parent</option>
              <option value="Guardian">Guardian</option>
              <option value="Other adult">Other adult</option>
              <option value="None">None</option>
            </select>
          </label>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Car Seat Appropriate for Age/Weight</div>
            <div className="mt-1 flex gap-1">
              {(['yes', 'no', 'na'] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  disabled={locked}
                  onClick={() => onUpdate({ peds_car_seat_appropriate: v })}
                  className={cn(
                    'px-3 py-1 text-xs font-bold uppercase tracking-widest border',
                    epcr.peds_car_seat_appropriate === v
                      ? 'bg-violet-500 text-white border-violet-300'
                      : 'bg-slate-800 text-slate-400 border-slate-600 hover:bg-slate-700',
                  )}
                >
                  {v === 'na' ? 'N/A' : v}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Suspected NAT */}
        <div className="border border-slate-700 bg-slate-900/60 px-3 py-2">
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400">
                Suspected Non-Accidental Trauma
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5 leading-snug">
                RI law requires EMS mandatory reporting of suspected child abuse.
                If Yes, document DCYF notification below.
              </div>
            </div>
            <div className="flex gap-1">
              {(['yes', 'no', 'unable'] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  disabled={locked}
                  onClick={() => onUpdate({ peds_suspected_nat: v })}
                  className={cn(
                    'px-3 py-1 text-xs font-bold uppercase tracking-widest border',
                    epcr.peds_suspected_nat === v
                      ? v === 'yes'
                        ? 'bg-red-600 text-white border-red-400'
                        : 'bg-slate-700 text-slate-100 border-slate-500'
                      : 'bg-slate-800 text-slate-400 border-slate-600 hover:bg-slate-700',
                  )}
                >
                  {v === 'unable' ? 'Unable' : v}
                </button>
              ))}
            </div>
          </div>
          {natFlagged && (
            <div className="mt-3 border-t border-red-800/40 pt-2.5 space-y-2">
              <div className="flex items-center gap-2 text-red-300 text-xs font-bold uppercase tracking-widest">
                <AlertTriangle size={12} />
                DCYF Notification
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                <label className="flex flex-col gap-1">
                  <span className="text-[10px] uppercase tracking-widest text-slate-500">Reporter Name</span>
                  <input
                    type="text"
                    value={epcr.peds_dcyf_reporter_name ?? ''}
                    onChange={(e) => onUpdate({ peds_dcyf_reporter_name: e.target.value || null })}
                    disabled={locked}
                    className="input-field text-sm"
                    placeholder="Name of person filing the report"
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-[10px] uppercase tracking-widest text-slate-500">Report Time</span>
                  <div className="flex gap-1">
                    <input
                      type="datetime-local"
                      value={epcr.peds_dcyf_report_time ? toLocal(epcr.peds_dcyf_report_time) : ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        onUpdate({ peds_dcyf_report_time: v ? new Date(v).toISOString() : null });
                      }}
                      disabled={locked}
                      className="input-field flex-1 text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => onUpdate({ peds_dcyf_report_time: new Date().toISOString() })}
                      disabled={locked}
                      className="px-2 border border-slate-600 bg-slate-800/80 text-[10px] font-bold uppercase tracking-wider text-slate-300 hover:bg-slate-700"
                      title="Set to current time"
                    >
                      Now
                    </button>
                  </div>
                </label>
              </div>
            </div>
          )}
        </div>

        {/* Destination hint */}
        <div className="border border-violet-800/40 bg-violet-950/30 px-3 py-2 text-xs text-violet-200">
          <span className="font-bold uppercase tracking-widest text-[10px] mr-1">Destination</span>
          ACS 2021 recommends a <strong>pediatric-capable trauma center</strong> for injured
          patients under 18. Hasbro Children&rsquo;s (Level I Pediatric) is the regional option.
          Confirm destination on the Disposition tab.
        </div>
      </div>
    </section>
  );
}

function toLocal(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
